import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./PasswordReset.css";

const PasswordReset = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [otp, setOtp] = useState("");
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [timer, setTimer] = useState(180);
  const [isOtpExpired, setIsOtpExpired] = useState(false);
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [passwordsMatch, setPasswordsMatch] = useState(false);

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  useEffect(() => {
    if (email) {
      if (!isValidEmail(email)) {
        setEmailError("รูปแบบอีเมลไม่ถูกต้อง");
      } else {
        setEmailError("");
      }
    } else {
      setEmailError("");
    }
  }, [email]);

  useEffect(() => {
    if (confirmPassword.length > 0) {
      if (password === confirmPassword) {
        setPasswordsMatch(true);
        setConfirmPasswordError("");
      } else {
        setPasswordsMatch(false);
        setConfirmPasswordError("รหัสผ่านไม่ตรงกัน ❌");
      }
    }
  }, [password, confirmPassword]);

  const validatePassword = (password, confirmPassword) => {
    if (passwordError) return false;

    if (password !== confirmPassword) {
      setConfirmPasswordError("รหัสผ่านไม่ตรงกัน ❌");
      return false;
    } else {
      setConfirmPasswordError("");
    }

    return true;
  };

  useEffect(() => {
    let timerInterval;
    if (isOtpSent && timer > 0) {
      timerInterval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      setIsOtpExpired(true);
    }
    return () => clearInterval(timerInterval);
  }, [isOtpSent, timer]);

  const sendOtp = async () => {
    if (!email) {
      setEmailError("กรุณากรอกอีเมล");
      return;
    }

    if (!isValidEmail(email)) {
      setEmailError("รูปแบบอีเมลไม่ถูกต้อง");
      return;
    }

    try {
      const res = await axios.post("/resetpassword", {
        email,
      });
      setIsOtpSent(true);
      setTimer(180);
      setIsOtpExpired(false);
      alert(res.data?.message || "ส่ง OTP เรียบร้อยแล้ว ✅");
    } catch (err) {
      alert(err.response?.data?.message || "ไม่สามารถส่ง OTP ได้");
    }
  };

  const verifyOtp = async () => {
    if (!otp) {
      setOtpError("กรุณากรอก OTP");
      return;
    }

    try {
      const res = await axios.post(
        "/resetpassword/verify-otp",
        {
          email,
          otp,
        }
      );

      if (res.data.success) {
        setIsOtpVerified(true);
        setOtpError("");
        alert("OTP ถูกต้อง ✅ กรุณาตั้งรหัสผ่านใหม่");
      } else {
        setOtpError("OTP ไม่ถูกต้อง ❌");
      }
    } catch (err) {
      setOtpError("เกิดข้อผิดพลาดในการยืนยัน OTP");
    }
  };

  const handlePasswordReset = async () => {
    if (!isOtpVerified) {
      alert("กรุณายืนยัน OTP ก่อนทำการตั้งรหัสผ่านใหม่");
      return;
    }

    if (!validatePassword(password, confirmPassword)) return;

    try {
      const res = await axios.post(
        "/resetpassword/reset-password",
        {
          email,
          newPassword: password,
        }
      );

      if (res.data.success) {
        alert("เปลี่ยนรหัสผ่านสำเร็จ ✅");
        navigate("/support");
      } else {
        alert("เปลี่ยนรหัสผ่านไม่สำเร็จ ❌");
      }
    } catch (err) {
      alert("เกิดข้อผิดพลาด: " + err.message);
    }
  };

  return (
    <div className="reset-container">
      <h2>Reset Your Password</h2>

      <div className="form-field">
        <label>อีเมล</label>
        <input
          type="email"
          placeholder="กรอก Email ที่ลงทะเบียนไว้"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isOtpSent}
          className={emailError ? "input-error" : ""}
        />
        {emailError && <div className="error-message">{emailError}</div>}
      </div>

      {!isOtpSent && (
        <button
          onClick={sendOtp}
          className="primary-button"
          disabled={emailError || !email}
        >
          ส่ง OTP
        </button>
      )}

      {isOtpSent && (
        <>
          <div className="form-field">
            <label>รหัส OTP</label>
            <input
              type="text"
              placeholder="กรอก OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              disabled={isOtpVerified}
              className={otpError ? "input-error" : ""}
            />
            {otpError && <div className="error-message">{otpError}</div>}
            {!isOtpVerified && (
              <div className="timer">
                เวลาที่เหลือ: {Math.floor(timer / 60)}:
                {(timer % 60).toString().padStart(2, "0")}
                {isOtpExpired && (
                  <span className="expired-text"> (หมดเวลา)</span>
                )}
              </div>
            )}
          </div>

          {!isOtpVerified && (
            <div className="button-group">
              <button
                onClick={verifyOtp}
                disabled={isOtpExpired || !otp}
                className="primary-button"
              >
                ยืนยัน OTP
              </button>
              <button onClick={sendOtp} className="secondary-button">
                ส่ง OTP ใหม่
              </button>
            </div>
          )}

          {isOtpVerified && (
            <>
              <div className="verification-success">
                ✅ การยืนยัน OTP สำเร็จ กรุณาตั้งรหัสผ่านใหม่
              </div>

              <div className="form-field">
                <label>รหัสผ่านใหม่</label>
                <input
                  type="password"
                  placeholder="รหัสผ่านใหม่"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={passwordError ? "input-error" : ""}
                />
                {passwordError && (
                  <div className="error-message">{passwordError}</div>
                )}
                <div className="password-requirements">
                  <p>รหัสผ่านต้องประกอบด้วย:</p>
                  <ul>
                    <li
                      className={password.length >= 8 ? "requirement-met" : ""}
                    >
                      อย่างน้อย 8 ตัวอักษร
                    </li>
                    <li
                      className={
                        /[A-Z]/.test(password) ? "requirement-met" : ""
                      }
                    >
                      ตัวอักษรพิมพ์ใหญ่
                    </li>
                    <li
                      className={
                        /[a-z]/.test(password) ? "requirement-met" : ""
                      }
                    >
                      ตัวอักษรพิมพ์เล็ก
                    </li>
                    <li
                      className={/\d/.test(password) ? "requirement-met" : ""}
                    >
                      ตัวเลข
                    </li>
                    <li
                      className={
                        /[!@#$%^&*(),.?":{}|<>]/.test(password)
                          ? "requirement-met"
                          : ""
                      }
                    >
                      อักขระพิเศษ
                    </li>
                  </ul>
                </div>
              </div>

              <div className="form-field">
                <label>ยืนยันรหัสผ่านใหม่</label>
                <input
                  type="password"
                  placeholder="ยืนยันรหัสผ่านใหม่"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={
                    confirmPasswordError
                      ? "input-error"
                      : passwordsMatch && confirmPassword
                      ? "input-success"
                      : ""
                  }
                />
                {confirmPasswordError && (
                  <div className="error-message">{confirmPasswordError}</div>
                )}
                {passwordsMatch && confirmPassword && (
                  <div className="success-message">รหัสผ่านตรงกัน ✅</div>
                )}
              </div>

              <button
                onClick={handlePasswordReset}
                disabled={!passwordsMatch || !password || !!passwordError}
                className="primary-button"
              >
                ตั้งรหัสผ่านใหม่
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default PasswordReset;
